/**
 * V2 EmptyState — the `.empty` treatment from design_reference. Centered
 * icon tile + title + optional description and action slot. Used for empty
 * tables and non-redesigned routes ("Not in the new interface yet").
 */

import type { ReactNode } from 'react';
import { cn } from '../../../lib/utils';

/** Props for the V2 EmptyState. */
export interface EmptyStateProps {
  /** Icon node rendered in the rounded tile. */
  icon?: ReactNode;
  title: string;
  description?: ReactNode;
  /** Optional action(s) (e.g. a Button). */
  action?: ReactNode;
  className?: string;
}

/** V2 empty / zero-data state. */
export function EmptyState({ icon, title, description, action, className }: EmptyStateProps) {
  return (
    <div className={cn('text-center py-[60px] px-5 text-[var(--tx-lo)]', className)}>
      {icon && (
        <div
          className={cn(
            'grid place-items-center w-[46px] h-[46px] rounded-[12px] mx-auto mb-[14px]',
            'bg-[var(--bg-3)] border border-[var(--bd-subtle)] text-[var(--tx-lo)]',
            '[&_svg]:w-[22px] [&_svg]:h-[22px]',
          )}
        >
          {icon}
        </div>
      )}
      <div className="text-[var(--tx-hi)] font-[550] text-[14px]">{title}</div>
      {description && <div className="mt-1.5 text-[12.5px] text-[var(--tx-lo)]">{description}</div>}
      {action && <div className="mt-4 flex justify-center">{action}</div>}
    </div>
  );
}
